import React, { useState, useEffect } from 'react';
import { UserIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../contexts/TitleContext';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { setTitle, resetTitle } = usePageTitle();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    setTitle('Вход');
  }, [setTitle]);

  useEffect(() => () => resetTitle(), [resetTitle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const tokenResponse = await fetch(`${API_BASE_URL}/api/v1/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          username,
          password,
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error('Неверный логин или пароль');
      }

      const tokenData = await tokenResponse.json();
      const accessToken = `Bearer ${tokenData.access_token}`;
      localStorage.setItem('token', accessToken);

      const userResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': accessToken,
        },
      });

      if (!userResponse.ok) {
        throw new Error('Ошибка получения данных пользователя');
      }

      const userData = await userResponse.json();
      localStorage.setItem('user', JSON.stringify(userData));

      onLogin();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Ошибка при входе. Попробуйте позже.');
      localStorage.removeItem('token');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-blue-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="px-8 py-14 relative">

          <div className="text-center mb-6">
            <div className="mb-8">
              <span style={{
                fontWeight: 'bold',
                color: 'rgb(39, 131, 204)',
                fontSize: '1.8rem',
                display: 'block'
              }}>
                ЭНЕРГИЯ-СДО.РФ
              </span>
              <span className="text-gray-500 text-sm font-medium mt-2 block">
                Управление обучением
              </span>
            </div>
            <h2 className="text-2xl font-medium text-gray-900">Вход в систему</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div className="relative mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={() => setUsernameFocused(true)}
                  onBlur={() => setUsernameFocused(!!username)}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                    usernameFocused || username ? 'border-blue-500' : ''
                  }`}
                  placeholder=" "
                />
                <label
                  htmlFor="username"
                  className={`absolute left-10 text-sm transition-all duration-200 pointer-events-none ${
                    usernameFocused || username
                      ? 'text-xs text-blue-600 bg-white px-1 -translate-y-1/2 transform'
                      : 'text-gray-500'
                  }`}
                  style={{
                    top: (usernameFocused || username) ? '-1px' : '50%',
                    transform: (usernameFocused || username)
                      ? 'translateY(-50%)'
                      : 'translateY(-50%)',
                  }}
                >
                  Логин (Email)
                </label>
              </div>
            </div>
            {/* Password */}
            <div className="relative mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(!!password)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                    passwordFocused || password ? 'border-blue-500' : ''
                  }`}
                  placeholder=" "
                />
                <label
                  htmlFor="password"
                  className={`absolute left-10 text-sm transition-all duration-200 pointer-events-none ${
                    passwordFocused || password
                      ? 'text-xs text-blue-600 bg-white px-1 -translate-y-1/2 transform'
                      : 'text-gray-500'
                  }`}
                  style={{
                    top: (passwordFocused || password) ? '-1px' : '50%',
                    transform: (passwordFocused || password)
                      ? 'translateY(-50%)'
                      : 'translateY(-50%)',
                  }}
                >
                  Пароль
                </label>
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200 animate-fadeIn">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium text-white shadow-md transition-all duration-200 ${
                isLoading
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Вход...
                </span>
              ) : (
                'Войти'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;